import type { AssessmentReportRecord } from '@really-assess/shared/integrations'

export type AssessmentReportFetchResult =
  | { available: true; report: AssessmentReportRecord }
  | { available: false; report: null }

export async function fetchAssessmentReport(
  sessionId: string,
  sessionToken: string
): Promise<AssessmentReportFetchResult | null> {
  try {
    const response = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/assessment-report`, {
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    })

    if (response.status === 404) {
      return { available: false, report: null }
    }

    if (!response.ok) {
      console.warn('[AssessmentReport] Fetch failed:', response.status)
      return null
    }

    const data = (await response.json()) as {
      available?: boolean
      report?: AssessmentReportRecord | null
    }

    if (data.available && data.report) {
      return { available: true, report: data.report }
    }

    return { available: false, report: null }
  } catch (error) {
    console.error('[AssessmentReport] Network error:', error)
    return null
  }
}

export type WaitForAssessmentReportOptions = {
  /** Max time to wait for the webhook-backed report (ms). Default 45s. */
  timeoutMs?: number
  /** Delay between polls (ms). Default 1.5s. */
  intervalMs?: number
  onAttempt?: (attempt: number) => void
}

/**
 * Poll until ReallyAvatar's report webhook has been stored for this session,
 * or until timeout. Used to gate navigation to Results.
 */
export async function waitForAssessmentReport(
  sessionId: string,
  sessionToken: string,
  options: WaitForAssessmentReportOptions = {}
): Promise<AssessmentReportRecord | null> {
  const timeoutMs = options.timeoutMs ?? 45_000
  const intervalMs = options.intervalMs ?? 1_500
  const started = Date.now()
  let attempt = 0

  while (Date.now() - started < timeoutMs) {
    attempt += 1
    options.onAttempt?.(attempt)

    const data = await fetchAssessmentReport(sessionId, sessionToken)
    if (data?.available && data.report) {
      return data.report
    }

    const remaining = timeoutMs - (Date.now() - started)
    if (remaining <= 0) break
    await new Promise((resolve) => setTimeout(resolve, Math.min(intervalMs, remaining)))
  }

  return null
}
